import { useCallback, useState } from "react";
import ChartFactory from "src/charts/ChartFactory";
import WidgetPlaceholder from "./WidgetPlaceholder";

type FunnelStage = {
  name: string;
  description?: string;
  count: number;
  percentage: number;
  color?: string;
  order?: number;
  breakdown?: Record<string, unknown>;
};

type StatCard = {
  label: string;
  value: number | string;
  color?: string;
};

type Insight = {
  variant: "danger" | "success" | "warning" | "info";
  title: string;
  stage?: string;
  detail?: string;
  primary_driver?: string;
  drivers?: AnalyticsDriver[];
};

type FunnelSummary = {
  stat_cards?: StatCard[];
  insights?: Insight[];
};

type AnalyticsDriver = {
  name: string;
  cases: number;
  percentage: number;
};

type LargestDropoffAnalytics = {
  stage?: string;
  lost?: number;
  conversion_rate?: number;
  primary_driver?: string;
  drivers?: AnalyticsDriver[];
};

type StageTatAnalytics = {
  stage: string;
  avg_days?: number;
  case_count?: number;
  included_in_tat?: number;
  missing_timestamp_count?: number;
  not_reached_count?: number;
};

type RankedAnalyticsItem = {
  name?: string;
  reason?: string;
  cases: number;
  percentage?: number;
  root_cause?: string;
};

type DelayedCaseOutlier = {
  case_id: number | string;
  current_stage: string;
  days_waiting: number;
  threshold_days: number;
  payer?: string;
  payer_segment?: string;
  root_cause?: string;
  delayed_stages?: string[];
};

type PayerBreakdowns = {
  top_payers?: RankedAnalyticsItem[];
  payer_tiers?: RankedAnalyticsItem[];
  payer_segments?: RankedAnalyticsItem[];
};

type FunnelAnalytics = {
  largest_dropoff?: LargestDropoffAnalytics;
  stage_tat?: StageTatAnalytics[];
  top_denial_reasons?: RankedAnalyticsItem[];
  payer_breakdowns?: PayerBreakdowns;
  delayed_case_outliers?: DelayedCaseOutlier[];
};

type FunnelApiData = {
  stages?: FunnelStage[];
  summary?: FunnelSummary;
  analytics?: FunnelAnalytics;
};

type FunnelWidgetProps = {
  isLoading: boolean;
  title: string;
  description?: string | null;
  data: unknown;
  widgetId?: string | number;
};

const toLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatNumber = (value?: number | string) =>
  typeof value === "number" ? value.toLocaleString() : value;

const formatDays = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(2)}d` : "-";

const formatPercent = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(1)}%` : "-";

const formatSegment = (value?: string) => (value ? toLabel(value) : "-");

const hasItems = <T,>(items?: T[]) => Array.isArray(items) && items.length > 0;

function BreakdownBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <div className="mb-1">
      <div
        className="d-flex justify-content-between align-items-center mb-1"
        style={{ fontSize: "0.72rem" }}
      >
        <span className="text-body-secondary">{label}</span>
        <span className="fw-semibold">
          {value.toLocaleString()}{" "}
          <span className="text-muted fw-normal">({pct.toFixed(1)}%)</span>
        </span>
      </div>
      <div
        className="rounded-pill overflow-hidden"
        style={{ height: 6, background: "var(--bs-border-color)" }}
      >
        <div
          className="rounded-pill h-100"
          style={{
            width: `${pct}%`,
            background: color,
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}

function BreakdownSection({
  breakdown,
  stageCount,
  stageColor,
}: {
  breakdown: Record<string, unknown>;
  stageCount: number;
  stageColor: string;
}) {
  const numericEntries = Object.entries(breakdown).filter(
    ([, v]) => typeof v === "number",
  ) as [string, number][];

  const objectEntries = Object.entries(breakdown).filter(
    ([, v]) => typeof v === "object" && v !== null && !Array.isArray(v),
  ) as [string, Record<string, unknown>][];

  const numericTotal =
    numericEntries.reduce((s, [, v]) => s + v, 0) || stageCount;

  return (
    <div>
      {numericEntries.map(([key, val]) => (
        <BreakdownBar
          key={key}
          label={toLabel(key)}
          value={val}
          total={numericTotal}
          color={stageColor}
        />
      ))}
      {objectEntries.map(([sectionKey, sectionVal]) => {
        const subEntries = Object.entries(sectionVal).filter(
          ([, v]) => typeof v === "number",
        ) as [string, number][];
        if (!subEntries.length) return null;
        const subTotal =
          subEntries.reduce((s, [, v]) => s + v, 0) || stageCount;
        return (
          <div key={sectionKey} className="mt-2">
            <div
              className="text-muted mb-1"
              style={{
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {toLabel(sectionKey)}
            </div>
            {subEntries.map(([k, v]) => (
              <BreakdownBar
                key={k}
                label={toLabel(k)}
                value={v}
                total={subTotal}
                color={stageColor}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function AnalyticsRankedList({
  items,
  labelKey,
  emptyText,
  limit = 5,
  headerLabel,
}: {
  items?: RankedAnalyticsItem[];
  labelKey: "name" | "reason";
  emptyText: string;
  limit?: number | null;
  headerLabel?: string;
}) {
  const rankedItems = items ?? [];
  const visibleItems = limit == null ? rankedItems : rankedItems.slice(0, limit);

  if (!visibleItems.length) {
    return (
      <div className="text-muted py-2 text-center" style={{ fontSize: "0.72rem" }}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-1">
      {headerLabel && (
        <div
          className="d-flex align-items-center justify-content-between gap-2 text-muted fw-semibold border-bottom pb-1"
          style={{ fontSize: "0.66rem" }}
        >
          <span>{headerLabel}</span>
          <span>Cases</span>
        </div>
      )}
      {visibleItems.map((item, index) => {
        const label = item[labelKey] ?? "Unknown";
        return (
          <div
            key={`${label}-${index}`}
            className="d-flex align-items-start justify-content-between gap-2 border-bottom pb-1"
            style={{ fontSize: "0.72rem" }}
          >
            <div className="text-truncate">
              <span className="text-muted me-1">{index + 1}.</span>
              <span className="text-body">{label}</span>
              {item.root_cause && (
                <div className="text-muted text-truncate" style={{ fontSize: "0.66rem" }}>
                  {item.root_cause}
                </div>
              )}
            </div>
            <div className="text-end flex-shrink-0">
              <span className="fw-semibold">{item.cases.toLocaleString()}</span>
              {typeof item.percentage === "number" && (
                <span className="text-muted ms-1">({formatPercent(item.percentage)})</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type AnalyticsTab = "dropoff" | "tat" | "denials" | "payers" | "outliers";

function AdditionalAnalyticsPanel({
  analytics,
  activeTab,
  onTabChange,
}: {
  analytics: FunnelAnalytics;
  activeTab: AnalyticsTab;
  onTabChange: (tab: AnalyticsTab) => void;
}) {
  const payerBreakdowns = analytics.payer_breakdowns ?? {};
  const availableTabs: { id: AnalyticsTab; label: string; icon: string }[] = [
    hasItems(payerBreakdowns.top_payers) ||
    hasItems(payerBreakdowns.payer_segments) ||
    hasItems(payerBreakdowns.payer_tiers)
      ? { id: "payers", label: "Payers", icon: "bi-buildings" }
      : null,
    hasItems(analytics.delayed_case_outliers)
      ? { id: "outliers", label: "Outliers", icon: "bi-clock-history" }
      : null,
    hasItems(analytics.stage_tat)
      ? { id: "tat", label: "TAT", icon: "bi-stopwatch" }
      : null,
    hasItems(analytics.top_denial_reasons)
      ? { id: "denials", label: "Denials", icon: "bi-exclamation-triangle" }
      : null,
    analytics.largest_dropoff?.lost != null
      ? { id: "dropoff", label: "Drop", icon: "bi-arrow-down-right-circle" }
      : null,
  ].filter(Boolean) as { id: AnalyticsTab; label: string; icon: string }[];

  if (!availableTabs.length) return null;

  const resolvedActiveTab = availableTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : availableTabs[0].id;

  return (
    <div className="mt-2">
      <div className="d-flex gap-1 overflow-auto pb-1">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`btn btn-sm d-inline-flex align-items-center gap-1 px-2 py-1 ${
              tab.id === resolvedActiveTab ? "btn-primary" : "btn-outline-secondary"
            }`}
            style={{ fontSize: "0.68rem", whiteSpace: "nowrap" }}
            onClick={() => onTabChange(tab.id)}
          >
            <i className={`bi ${tab.icon}`} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="pt-1">
        {resolvedActiveTab === "dropoff" && (
          <LargestDropoffPanel dropoff={analytics.largest_dropoff} />
        )}
        {resolvedActiveTab === "tat" && (
          <StageTatPanel rows={analytics.stage_tat ?? []} />
        )}
        {resolvedActiveTab === "denials" && (
          <AnalyticsRankedList
            items={analytics.top_denial_reasons}
            labelKey="reason"
            emptyText="No denial reasons returned."
          />
        )}
        {resolvedActiveTab === "payers" && (
          <PayerBreakdownPanel breakdowns={payerBreakdowns} />
        )}
        {resolvedActiveTab === "outliers" && (
          <DelayedOutliersPanel rows={analytics.delayed_case_outliers ?? []} />
        )}
      </div>
    </div>
  );
}

function LargestDropoffPanel({
  dropoff,
}: {
  dropoff?: LargestDropoffAnalytics;
}) {
  if (!dropoff) return null;

  return (
    <div className="rounded px-2 py-2" style={{ background: "rgba(239,68,68,0.08)" }}>
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          <div className="text-danger fw-semibold" style={{ fontSize: "0.75rem" }}>
            {dropoff.stage ?? "Largest Drop"}
          </div>
          <div className="text-muted" style={{ fontSize: "0.68rem" }}>
            Primary driver: {dropoff.primary_driver ?? "Unknown"}
          </div>
        </div>
        <div className="text-end flex-shrink-0" style={{ fontSize: "0.72rem" }}>
          <div className="fw-semibold">{formatNumber(dropoff.lost)} lost</div>
          <div className="text-muted">
            {typeof dropoff.conversion_rate === "number"
              ? `${dropoff.conversion_rate}% conv.`
              : "Conversion unavailable"}
          </div>
        </div>
      </div>
      {hasItems(dropoff.drivers) && (
        <div className="mt-2">
          {dropoff.drivers!.map((driver) => (
            <BreakdownBar
              key={driver.name}
              label={driver.name}
              value={driver.cases}
              total={dropoff.lost ?? driver.cases}
              color="#dc3545"
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StageTatPanel({ rows }: { rows: StageTatAnalytics[] }) {
  const visibleRows = rows.filter((row) => row.stage !== "Referral");

  return (
    <div className="table-responsive">
      <table className="table table-sm mb-0 align-middle" style={{ fontSize: "0.68rem" }}>
        <thead>
          <tr>
            <th className="border-0 text-muted fw-semibold">Stage</th>
            <th className="border-0 text-muted fw-semibold text-end">Avg</th>
            <th className="border-0 text-muted fw-semibold text-end">Included</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <tr key={row.stage}>
              <td className="text-truncate" style={{ maxWidth: 120 }}>
                {row.stage}
                {typeof row.missing_timestamp_count === "number" &&
                  row.missing_timestamp_count > 0 && (
                    <span className="text-muted ms-1">
                      ({row.missing_timestamp_count} missing)
                    </span>
              )}
              </td>
              <td className="text-end">{formatDays(row.avg_days)}</td>
              <td className="text-end">
                {formatNumber(row.included_in_tat)}/{formatNumber(row.case_count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayerBreakdownPanel({ breakdowns }: { breakdowns: PayerBreakdowns }) {
  const [activePayerTab, setActivePayerTab] = useState<
    "top_payers" | "payer_segments" | "payer_tiers"
  >("top_payers");

  const payerTabs = [
    hasItems(breakdowns.top_payers)
      ? { id: "top_payers" as const, label: "Payers", labelKey: "name" as const }
      : null,
    hasItems(breakdowns.payer_segments)
      ? { id: "payer_segments" as const, label: "Segments", labelKey: "name" as const }
      : null,
    hasItems(breakdowns.payer_tiers)
      ? { id: "payer_tiers" as const, label: "Tiers", labelKey: "name" as const }
      : null,
  ].filter(Boolean) as {
    id: "top_payers" | "payer_segments" | "payer_tiers";
    label: string;
    labelKey: "name";
  }[];

  if (!payerTabs.length) return null;

  const resolvedTab = payerTabs.some((tab) => tab.id === activePayerTab)
    ? activePayerTab
    : payerTabs[0].id;
  const headerLabelByTab = {
    top_payers: "Payer",
    payer_segments: "Segment",
    payer_tiers: "Tier",
  };

  return (
    <div>
      {payerTabs.length > 1 && (
        <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Payer breakdown">
          {payerTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`btn ${tab.id === resolvedTab ? "btn-secondary" : "btn-outline-secondary"}`}
              style={{ fontSize: "0.66rem" }}
              onClick={() => setActivePayerTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <AnalyticsRankedList
        items={breakdowns[resolvedTab]}
        labelKey="name"
        emptyText="No payer breakdown returned."
        limit={resolvedTab === "top_payers" ? null : 5}
        headerLabel={headerLabelByTab[resolvedTab]}
      />
    </div>
  );
}

function DelayedOutliersPanel({ rows }: { rows: DelayedCaseOutlier[] }) {
  const outlierGridColumns = "72px minmax(90px, 1.3fr) minmax(80px, 0.9fr) 54px";

  return (
    <div className="d-flex flex-column gap-1">
      <div
        className="d-grid align-items-center gap-2 text-muted fw-semibold border-bottom pb-1"
        style={{ fontSize: "0.66rem", gridTemplateColumns: outlierGridColumns }}
      >
        <span>Case</span>
        <span>Payer</span>
        <span>Segment</span>
        <span className="text-end">Days</span>
      </div>
      {rows.map((row, index) => (
        <div
          key={`${row.case_id}-${row.current_stage}-${index}`}
          className="d-grid align-items-center gap-2 border-bottom pb-1"
          style={{ fontSize: "0.7rem", gridTemplateColumns: outlierGridColumns }}
        >
          <span className="fw-semibold text-truncate">#{row.case_id}</span>
          <span className="text-muted text-truncate" style={{ fontSize: "0.65rem" }}>
            {row.payer ?? "Unknown payer"}
          </span>
          <span className="text-muted text-truncate" style={{ fontSize: "0.65rem" }}>
            {formatSegment(row.payer_segment)}
          </span>
          <span className="fw-semibold text-warning text-end">
            {formatDays(row.days_waiting)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function FunnelWidget({
  isLoading,
  title,
  description,
  data,
  widgetId,
}: FunnelWidgetProps) {
  const [selectedStage, setSelectedStage] = useState<FunnelStage | null>(null);
  const [activeAnalyticsTab, setActiveAnalyticsTab] =
    useState<AnalyticsTab>("payers");

  const handleStageClick = useCallback((stage: FunnelStage) => {
    setSelectedStage((prev) => (prev?.name === stage.name ? null : stage));
  }, []);

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={320} />;
  }

  const funnelData = data as FunnelApiData;
  const summary = funnelData?.summary;
  const analytics = funnelData?.analytics;
  const hasBreakdown =
    selectedStage?.breakdown != null &&
    Object.keys(selectedStage.breakdown).length > 0;

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {/* Chart */}
      <div className="flex-fill" style={{ minHeight: 0 }}>
        <ChartFactory
          chartType="funnel"
          chartId={`${widgetId ?? `${title?.replace(/\s+/g, "-") || "chart"}-funnel`}`}
          title={title}
          description={description}
          data={data}
          fillHeight={true}
          onPointClick={handleStageClick}
        />
      </div>

      {/* Stage breakdown panel */}
      {selectedStage && (
        <div
          className="border-top flex-shrink-0"
          style={{ maxHeight: "45%", overflowY: "auto" }}
        >
          <div className="px-3 pt-2 pb-3">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle flex-shrink-0"
                  style={{
                    width: 10,
                    height: 10,
                    background: selectedStage.color ?? "#6366f1",
                  }}
                />
                <span className="fw-semibold" style={{ fontSize: "0.82rem" }}>
                  {selectedStage.name}
                </span>
                <span
                  className="badge rounded-pill"
                  style={{
                    background: selectedStage.color ?? "#6366f1",
                    fontSize: "0.68rem",
                  }}
                >
                  {selectedStage.count.toLocaleString()} ·{" "}
                  {selectedStage.percentage}%
                </span>
              </div>
              <button
                className="btn-close"
                style={{ fontSize: "0.6rem" }}
                onClick={() => setSelectedStage(null)}
                aria-label="Close breakdown"
              />
            </div>

            {selectedStage.description && (
              <div className="text-muted mb-2" style={{ fontSize: "0.72rem" }}>
                {selectedStage.description}
              </div>
            )}

            {hasBreakdown ? (
              <BreakdownSection
                breakdown={selectedStage.breakdown!}
                stageCount={selectedStage.count}
                stageColor={selectedStage.color ?? "#6366f1"}
              />
            ) : (
              <div
                className="text-muted text-center py-2"
                style={{ fontSize: "0.75rem" }}
              >
                No breakdown available for this stage.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      {(summary || analytics) && !selectedStage && (
        <div
          className="border-top pt-2 mt-1 flex-shrink-0 px-1"
          style={{ maxHeight: "52%", overflowY: "auto" }}
        >
          {summary?.stat_cards && summary.stat_cards.length > 0 && (
            <div className="row g-2 mb-2">
              {summary.stat_cards.map((card, i) => (
                <div key={i} className="col">
                  <div
                    className="text-center p-2 rounded"
                    style={{ background: "var(--bs-tertiary-bg)" }}
                  >
                    <div className="fw-bold fs-6" style={{ color: card.color }}>
                      {typeof card.value === "number"
                        ? card.value.toLocaleString()
                        : card.value}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                      {card.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {summary?.insights && summary.insights.length > 0 && (
            <div className="d-flex flex-column gap-1">
              {summary.insights.map((insight, i) => {
                const isDanger = insight.variant === "danger";
                return (
                  <div
                    key={i}
                    className="d-flex align-items-start gap-2 px-2 py-1 rounded"
                    style={{
                      background: isDanger
                        ? "rgba(239,68,68,0.08)"
                        : "rgba(16,185,129,0.08)",
                      fontSize: "0.72rem",
                    }}
                  >
                    <i
                      className={`bi ${isDanger ? "bi-arrow-down-circle-fill text-danger" : "bi-check-circle-fill text-success"} flex-shrink-0`}
                      style={{ fontSize: "0.75rem" }}
                    />
                    <div>
                      <span
                        className={`${isDanger ? "text-danger" : "text-success"} fw-semibold`}
                      >
                        {insight.title}:{" "}
                      </span>
                      {insight.stage && (
                        <span className="text-body">{insight.stage}</span>
                      )}
                      {insight.detail && (
                        <span className="text-muted ms-1">
                          · {insight.detail}
                        </span>
                      )}
                      {insight.primary_driver && (
                        <div className="text-muted" style={{ fontSize: "0.66rem" }}>
                          Driver: {insight.primary_driver}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {analytics && (
            <AdditionalAnalyticsPanel
              analytics={analytics}
              activeTab={activeAnalyticsTab}
              onTabChange={setActiveAnalyticsTab}
            />
          )}
        </div>
      )}
    </div>
  );
}
